import {
  GridActionsCellItem,
  GridColumns,
  GridRenderCellParams,
  GridRowParams
} from '@mui/x-data-grid-premium';
import DataGridCell from 'components/common/DataGridCell';
import { useTranslation } from 'react-i18next';
import { printLabels } from 'services/zebra';
import {
  booleanFilterOperators,
  dateFilterOperators,
  filterOperators,
  numberFilterOperators,
  ProductWarehouseStatus
} from '../../../../constants';
import LocalPrintshopIcon from '@mui/icons-material/LocalPrintshop';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import { baseCell } from '../cells/base-cell';
import { booleanCell } from '../cells/boolean-cell';
import dayjs from 'dayjs';
import { IStatusOption, IStockQuantity } from 'interfaces';
import { adminCell } from '../cells/admin-only-base-cell';

const getContractLines = (params) => {
  return params.row.contract_lines;
};

export const StockQuantitiesColumns = (isAdmin = false, locked?: boolean): GridColumns => {
  const translationPrefix = 'entities.stock_quantity';
  const statusOptions: IStatusOption[] = Object.entries(ProductWarehouseStatus).map(
    ([display_name, key]) => {
      return {
        value: Number(key),
        display_name: String(display_name)
      };
    }
  );
  const { t } = useTranslation();
  return [
    {
      field: 'actions',
      type: 'actions',
      headerName: t(`global.actions`),
      getActions: (params: GridRowParams) => {
        const actions = [
          <GridActionsCellItem
            key={1}
            icon={<LocalPrintshopIcon />}
            onClick={() =>
              printLabels(
                params.row['product'],
                params.row['product_batch'],
                params.row['expiration_date'],
                params.row['quantity'],
                params.row['description']
              )
            }
            label={t('global.print_labels')}
          />,
          <GridActionsCellItem
            key={2}
            icon={<UnfoldLessIcon />}
            onClick={() =>
              printLabels(
                params.row['product'],
                params.row['product_batch'],
                params.row['expiration_date'],
                params.row['quantity'],
                params.row['description'],
                true
              )
            }
            label={t('global.print_barcode')}
          />
        ];
        return actions;
      },
      filterable: false
    },
    {
      field: 'quantity',
      filterOperators: numberFilterOperators,
      headerName: t(`${translationPrefix}.quantity`),
      type: 'number',
      filterable: true,
      sortable: true,
      editable: isAdmin,
      renderCell: (params: GridRenderCellParams<string>) => {
        const movement = params.row as IStockQuantity;
        return <>{movement.quantity}</>;
      }
    },
    {
      field: 'product',
      headerName: t(`global.product`),
      width: 200,
      filterOperators,
      sortable: !locked,
      filterable: !locked,
      renderCell: baseCell
    },
    {
      field: 'description',
      headerName: t(`global.description`),
      width: 200,
      filterOperators,
      sortable: !locked,
      filterable: !locked,
      renderCell: baseCell
    },
    {
      field: 'status',
      headerName: t(`global.status`),
      width: 200,
      type: 'singleSelect',
      valueOptions: statusOptions.map((opt) => {
        return {
          value: String(opt.value),
          label: t(`entities.product.status_options.${opt.value}`)
        };
      }),
      filterable: !locked,
      sortable: true,
      renderCell: ({ value }) => (
        <span className={value === 'ACTIVE' ? 'text-green-500' : 'text-orange-500'}>
          {t(`entities.product.status_options.${value}`)}
        </span>
      )
    },
    {
      field: 'notes',
      headerName: t(`global.notes`),
      width: 200,
      filterOperators,
      filterable: !locked,
      renderCell: baseCell
    },
    {
      field: 'order_notes',
      headerName: t(`entities.product.order_notes`),
      width: 200,
      filterOperators,
      sortable: !locked,
      filterable: !locked,
      renderCell: baseCell
    },
    {
      field: 'category',
      headerName: t(`global.category`),
      width: 200,
      filterOperators,
      renderCell: baseCell
    },
    {
      field: 'warehouse',
      headerName: t(`global.warehouse`),
      width: 200,
      filterOperators,
      renderCell: baseCell
    },
    {
      field: 'stock_quantity_locations',
      headerName: t(`entities.stock_quantity.locations`),
      width: 180,
      filterable: false,
      sortable: false,
      valueGetter: (params) => {
        const sqLocs = params.row?.stock_quantity_locations;
        return Array.isArray(sqLocs) && sqLocs.length > 0 ? sqLocs.join(', ') : '';
      },
      renderCell: (params: GridRenderCellParams) => {
        const sqLocs = params.row?.stock_quantity_locations;
        const text = Array.isArray(sqLocs) && sqLocs.length > 0 ? sqLocs.join(', ') : '';
        return <>{text}</>;
      }
    },
    {
      field: 'suppliers',
      valueGetter: getContractLines,
      headerName: t(`${translationPrefix}.suppliers`),
      renderCell: (params: GridRenderCellParams) => {
        const list = Array.isArray(params.value) ? params.value : [];
        const suppliers = list.map(({ supplier }) => supplier);
        return <DataGridCell content={suppliers.join(',')} />;
      },
      valueFormatter: (params) => {
        const raw =
          params != null && typeof params === 'object' && 'value' in params ? params.value : params;
        const list = Array.isArray(raw) ? raw : [];
        return list
          .map((item) => item?.supplier)
          .filter(Boolean)
          .join(', ');
      },
      filterOperators,
      sortable: !locked,
      filterable: !locked,
      width: 300
    },
    {
      field: 'product_batch',
      headerName: t(`${translationPrefix}.product_batch`),
      width: 200,
      filterOperators,
      renderCell: baseCell
    },
    {
      field: 'expiration_date',
      headerName: t(`${translationPrefix}.expiration_date`),
      filterOperators: dateFilterOperators,
      valueFormatter: (params) => params.value && dayjs(params.value).format('DD/MM/YYYY'),
      editable: false,
      width: 200,
      type: 'date'
    },
    {
      field: 'discounted',
      headerName: t(`${translationPrefix}.discounted`),
      width: 200,
      filterOperators: booleanFilterOperators,
      renderCell: booleanCell(t)
    },
    {
      field: 'movement_unit_price',
      headerName: t(`${translationPrefix}.movement_unit_price`),
      width: 150,
      filterOperators: numberFilterOperators,
      filterable: true,
      sortable: true,
      renderCell: adminCell
    },
    {
      field: 'amount',
      headerName: t(`global.amount`),
      width: 150,
      type: 'number',
      filterOperators: numberFilterOperators,
      filterable: true,
      sortable: true,
      editable: isAdmin,
      renderCell: adminCell
    }
  ];
};
